import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Rocket, CheckCircle, Calendar, Target, Smartphone, Shield, ArrowRight, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export default function GrowthProgram() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", session.user.id)
            .single();
          setCurrentUser(profile);
        }
      } catch (error) {
        console.error("Error loading user:", error);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  const hasPurchased = currentUser?.growth_package_purchased_at;
  const purchaseDate = hasPurchased ? new Date(currentUser.growth_package_purchased_at) : null;
  const refundEligibleDate = purchaseDate ? new Date(purchaseDate.getTime() + 365 * 24 * 60 * 60 * 1000) : null;

  return (
    <div className="min-h-screen px-4 py-10 pb-32">
      <div className="max-w-4xl mx-auto space-y-12">
        {/* Hero Section */}
        <header className="text-center space-y-4">
          <div className="inline-flex items-center justify-center gap-3 neuro-base rounded-full px-6 py-3 mb-4">
            <Rocket className="w-6 h-6 text-[#7f5af0]" />
            <span className="text-sm text-[#a0a0a0] uppercase tracking-wider">1-Year Growth Program</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#e0e0e0]">
            Guaranteed Growth<br />
            <span className="text-[#7f5af0]">If You Follow The Plan</span>
          </h1>
          <p className="text-lg text-[#909090] max-w-2xl mx-auto">
            We've helped hundreds of artists grow their audience. If you follow our plan exactly for 1 full year and see zero growth, you get a full refund.
          </p>
        </header>

        {/* Already Purchased Banner */}
        {hasPurchased && (
          <div className="neuro-base rounded-3xl p-6 border-2 border-[#7f5af0] bg-[#7f5af0]/10">
            <div className="flex items-center gap-4">
              <CheckCircle className="w-8 h-8 text-[#7f5af0] flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#e0e0e0]">You're Enrolled!</h3>
                <p className="text-[#a0a0a0]">
                  Started: {purchaseDate?.toLocaleDateString()} • 
                  Refund eligible after: {refundEligibleDate?.toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* What You Get */}
        <section className="neuro-base rounded-3xl p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-[#e0e0e0] mb-6 flex items-center gap-3">
            <Target className="w-6 h-6 text-[#7f5af0]" />
            What You Get
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "199 Premium Credits", desc: "Use for uploads, boosts, and playlist submissions" },
              { title: "1:1 Custom Launch Strategy", desc: "Personalized plan tailored to your music and goals" },
              { title: "Weekly Check-ins", desc: "Accountability calls to keep you on track" },
              { title: "Playlist & Creator Outreach", desc: "We handle the pitching for you" },
              { title: "Performance Dashboard", desc: "Track your growth metrics in real-time" },
              { title: "Companion Mobile App", desc: "FREE with purchase - required for compliance tracking", icon: Smartphone },
            ].map((item, i) => (
              <div key={i} className="neuro-pressed rounded-2xl p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-[#d0d0d0]">{item.title}</h4>
                  <p className="text-sm text-[#808080]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* What's Expected */}
        <section className="neuro-base rounded-3xl p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-[#e0e0e0] mb-6 flex items-center gap-3">
            <Calendar className="w-6 h-6 text-[#7f5af0]" />
            What's Expected of You
          </h2>
          <div className="space-y-4">
            {[
              { title: "Follow the plan 100%", desc: "No shortcuts. The plan works when you work the plan." },
              { title: "Use the companion app", desc: "Post on social media through our app so we can verify compliance." },
              { title: "Stay consistent for 12 months", desc: "Growth takes time. Commit to the full year." },
              { title: "Attend weekly check-ins", desc: "Show up, ask questions, and stay accountable." },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="neuro-flat w-8 h-8 rounded-full flex items-center justify-center text-[#7f5af0] font-bold flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h4 className="font-medium text-[#d0d0d0]">{item.title}</h4>
                  <p className="text-sm text-[#808080]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Refund Policy */}
        <section className="neuro-base rounded-3xl p-6 md:p-8 border border-[#3a3a3a]">
          <h2 className="text-2xl font-semibold text-[#e0e0e0] mb-6 flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#7f5af0]" />
            Growth Guarantee & Refund Policy
          </h2>
          <div className="space-y-4 text-[#b0b0b0]">
            <p>
              We're confident in our program because <strong className="text-[#e0e0e0]">it works</strong>. 
              Artists who follow the plan see real growth. But we understand you need peace of mind.
            </p>
            <div className="neuro-pressed rounded-2xl p-4 space-y-3">
              <h4 className="font-medium text-[#d0d0d0] flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#7f5af0]" />
                Refund Requirements
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#7f5af0]">•</span>
                  <span>Complete the <strong>full 12 months</strong> of the program</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7f5af0]">•</span>
                  <span>Maintain <strong>100% compliance</strong> with the plan (tracked via mobile app)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7f5af0]">•</span>
                  <span>Attend all weekly check-ins (or reschedule within 48 hours)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#7f5af0]">•</span>
                  <span>If after 12 months of full compliance you see <strong>zero growth</strong>, request a refund</span>
                </li>
              </ul>
            </div>
            <p className="text-sm text-[#808080]">
              No partial refunds. No early exits. The guarantee only applies if you follow the plan exactly for 12 full months.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center space-y-4">
          {loading ? (
            <div className="h-16 flex items-center justify-center text-[#808080]">
              Loading...
            </div>
          ) : hasPurchased ? (
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 text-green-400">
                <CheckCircle className="w-6 h-6" />
                <span className="text-lg font-medium">You're already enrolled in the Growth Program!</span>
              </div>
              <p className="text-[#808080]">
                Check your email for onboarding instructions or contact support if you need help.
              </p>
            </div>
          ) : (
            <>
              <Link
                to="/Shop"
                className="inline-flex items-center gap-3 neuro-base px-8 py-4 rounded-2xl text-lg font-semibold text-[#e0e0e0] hover:scale-105 transition-transform border border-[#7f5af0]"
              >
                Get Started - $199
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-sm text-[#808080]">
                One-time purchase • Cannot be purchased again • Full refund if you follow the plan and see zero growth after 12 months
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
